import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Eye, Pencil, Download, FileText, Printer } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, authUrl } from "../lib/api";
import { TAHUN_AJARAN, SEMESTER_LAPORAN, SECTIONS, SUBS, tglPanjang, todayISO } from "../lib/constants";
import { PageTitle, FilterBar, Field, Input, Select, Textarea, Table, Modal, FormActions, Loading } from "../components/Shared";

const emptySections = () => Object.fromEntries(SECTIONS.map(([k]) => [k, { kondisi: "", target: "", pencapaian: "" }]));

function LaporanForm({ init, siswaNama, onDone, onCancel }) {
  const [f, setF] = useState({ kelas: "", tahun_ajaran: TAHUN_AJARAN[0], semester: SEMESTER_LAPORAN[0], tanggal_penyusunan: todayISO(), ...init, sections: { ...emptySections(), ...(init?.sections || {}) } });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setS = (sec, sub) => (e) => setF({ ...f, sections: { ...f.sections, [sec]: { ...f.sections[sec], [sub]: e.target.value } } });
  const submit = async (e) => { e.preventDefault(); setSaving(true); try { init?.id ? await api.put(`/laporan/${init.id}`, f) : await api.post("/laporan", f); toast.success("Laporan disimpan"); onDone(); } catch (ex) { toast.error(errMsg(ex)); } finally { setSaving(false); } };
  return (
    <form onSubmit={submit} data-testid="laporan-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nama Siswa Pendampingan" className="sm:col-span-2"><Input value={siswaNama} disabled /></Field>
        <Field label="Kelas" required><Input data-testid="lap-kelas" value={f.kelas} onChange={set("kelas")} placeholder="cth: 4F" required /></Field>
        <Field label="Tanggal Penyusunan"><Input type="date" data-testid="lap-tanggal" value={f.tanggal_penyusunan} onChange={set("tanggal_penyusunan")} /></Field>
        <Field label="Tahun Ajaran"><Select data-testid="lap-tahun-ajaran" value={f.tahun_ajaran} onChange={set("tahun_ajaran")} options={TAHUN_AJARAN} /></Field>
        <Field label="Semester"><Select data-testid="lap-semester" value={f.semester} onChange={set("semester")} options={SEMESTER_LAPORAN} /></Field>
      </div>
      {SECTIONS.map(([k, title], i) => (
        <div key={k} className="mt-6 rounded-xl border border-emerald-200 overflow-hidden">
          <div className="bg-emerald-600 text-white px-4 py-2.5 text-sm font-bold tracking-wide">{String.fromCharCode(65 + i)}. {title}</div>
          <div className="p-4 space-y-4 bg-emerald-50/30">
            {SUBS.map(([sk, sl], j) => <Field key={sk} label={`${j + 1}. ${sl}`}><Textarea data-testid={`lap-${k}-${sk}`} value={f.sections[k][sk]} onChange={setS(k, sk)} rows={4} placeholder="Tulis catatan..." /></Field>)}
          </div>
        </div>
      ))}
      <FormActions onCancel={onCancel} saving={saving} />
    </form>
  );
}

function LaporanView({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/laporan/${id}`).then((r) => setD(r.data)); }, [id]);
  if (!d) return <Loading />;
  return (
    <div data-testid="laporan-view">
      <div className="flex flex-wrap gap-2 justify-end mb-4 no-print">
        <a className="btn-primary" href={authUrl(`/laporan/${id}/pdf`)} target="_blank" rel="noreferrer" data-testid="laporan-pdf-btn"><Printer size={16} /> Cetak / PDF</a>
        <a className="btn-outline" href={authUrl(`/laporan/${id}/docx`)} data-testid="laporan-docx-btn"><Download size={16} /> Download DOCX</a>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 font-serif text-[15px] text-slate-900">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h2 className="text-center font-bold text-lg mb-3">LAPORAN EVALUASI PENDAMPINGAN</h2>
            <table className="text-sm"><tbody>
              {[["Nama Siswa", d.siswa_nama], ["Kelas", `${d.kelas || "-"}  /  Semester : ${d.semester}`], ["Sekolah", d.sekolah], ["Guru Pendamping", d.guru_nama], ["Tanggal Penyusunan", tglPanjang(d.tanggal_penyusunan)]].map(([k, v]) => <tr key={k}><td className="pr-4 py-0.5 w-40">{k}</td><td className="pr-2">:</td><td className="border-b border-slate-400 pr-6">{v || "-"}</td></tr>)}
            </tbody></table>
          </div>
          <img src="/logo.webp" alt="" className="h-20 w-20 object-contain" />
        </div>
        {SECTIONS.map(([k, title], i) => (
          <div key={k} className="mt-6">
            <h3 className="font-bold mb-2">{String.fromCharCode(65 + i)}. {title}</h3>
            <div className="rounded-xl border-2 border-slate-800 p-4 space-y-3">
              {SUBS.map(([sk, sl], j) => <div key={sk}><div className="text-sm font-bold mb-1">{j + 1}. {sl}</div><p className="text-justify whitespace-pre-line leading-relaxed">{d.sections?.[k]?.[sk] || "-"}</p></div>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Laporan() {
  const { user } = useAuth();
  const isGuru = user.role === "guru";
  const F0 = { tahun_ajaran: "", semester: "" };
  const [filter, setFilter] = useState(F0);
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [siswaNama, setSiswaNama] = useState("");
  const load = useCallback((f = filter) => api.get("/laporan", { params: f }).then((r) => setRows(r.data.items)), [filter]);
  useEffect(() => { load(F0); if (isGuru) api.get("/siswa").then((r) => setSiswaNama(r.data.items[0]?.nama || "")); }, []); // eslint-disable-line
  const edit = async (r) => { const d = await api.get(`/laporan/${r.id}`); setModal({ type: "form", row: d.data }); };
  return (
    <div>
      <PageTitle title="Laporan Pendampingan" actions={isGuru && <button className="btn-primary" onClick={() => setModal({ type: "form" })} data-testid="laporan-add-btn"><Plus size={16} /> Input Laporan</button>} />
      <FilterBar onApply={() => load()} onReset={() => { setFilter(F0); load(F0); }}>
        <div className="w-44"><Select data-testid="filter-ta" value={filter.tahun_ajaran} onChange={(e) => setFilter({ ...filter, tahun_ajaran: e.target.value })} placeholder="Semua tahun ajaran" options={TAHUN_AJARAN} /></div>
        <div className="w-48"><Select data-testid="filter-semester" value={filter.semester} onChange={(e) => setFilter({ ...filter, semester: e.target.value })} placeholder="Semua semester" options={SEMESTER_LAPORAN} /></div>
      </FilterBar>
      <Table testId="laporan-table" rows={rows} cols={[
        { h: "Nama Siswa", k: "siswa_nama", brand: true },
        { h: "Guru Pendamping", k: "guru_nama" },
        { h: "Kelas", k: "kelas" },
        { h: "Tahun Ajaran", k: "tahun_ajaran" },
        { h: "Semester", k: "semester" },
        { h: "Tgl Penyusunan", render: (r) => tglPanjang(r.tanggal_penyusunan) },
        { h: "Lihat", nowrap: true, render: (r) => <div className="flex gap-1">
          <button className="btn-outline btn-sm" onClick={() => setModal({ type: "view", row: r })} data-testid={`laporan-view-${r.id}`}><Eye size={14} /> Lihat</button>
          <a className="btn-ghost btn-sm text-emerald-700" href={authUrl(`/laporan/${r.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`laporan-pdf-${r.id}`} title="PDF"><FileText size={15} /></a>
          <a className="btn-ghost btn-sm text-sky-700" href={authUrl(`/laporan/${r.id}/docx`)} data-testid={`laporan-docx-${r.id}`} title="DOCX"><Download size={15} /></a>
          {isGuru && <button className="btn-ghost btn-sm" onClick={() => edit(r)} data-testid={`laporan-edit-${r.id}`}><Pencil size={15} /></button>}
        </div> },
      ]} />
      <Modal open={modal?.type === "form"} onClose={() => setModal(null)} title={modal?.row ? "Edit Laporan" : "Input Laporan Pendampingan"} wide testId="laporan-modal"><LaporanForm init={modal?.row} siswaNama={siswaNama || modal?.row?.siswa_nama} onCancel={() => setModal(null)} onDone={() => { setModal(null); load(); }} /></Modal>
      <Modal open={modal?.type === "view"} onClose={() => setModal(null)} title="Laporan Evaluasi Pendampingan" wide testId="laporan-view-modal">{modal?.row && <LaporanView id={modal.row.id} />}</Modal>
    </div>
  );
}
